import AIModelsList from '@/components/common/ai-models-list';
import ProductFilter from '@/components/product/product-filter';
import ProductFilterAccordions from '@/components/product/product-filter-accordions';
import ProductSearch from '@/components/product/product-search';
import SearchResults from '@/components/search/search-results';
import routes from '@/config/routes';
import client from '@/data/client';
import { API_ENDPOINTS } from '@/data/client/endpoints';
import Layout from '@/layouts/_layout';
import Seo from '@/layouts/_seo';
import type {
  CategoryQueryOptions,
  NextPageWithLayout,
  ProductQueryOptions,
  SettingsQueryOptions,
} from '@/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { SiteLinksSearchBoxJsonLd } from 'next-seo';
import { useRouter } from 'next/router';
import { useRef } from 'react';
import { QueryClient, dehydrate } from 'react-query';
import { useSearchFocus } from '@/components/product/product-searchbar';
import { useBreakpoint } from '@/lib/hooks/use-breakpoint';
import cn from 'classnames';

export const getServerSideProps = async ({
  query,
  locale,
}: {
  query: any;
  locale: string;
}) => {
  const searchTerm = query.search ?? null;
  const description = query.search
    ? `View all AI prompts & assets containing ${searchTerm}`
    : null;

  const queryClient = new QueryClient();
  try {
    await Promise.all([
      queryClient.prefetchQuery(
        [API_ENDPOINTS.SETTINGS, { language: locale }],
        ({ queryKey }) =>
          client.settings.all(queryKey[1] as SettingsQueryOptions)
      ),
      // TODO: add search query?
      queryClient.prefetchInfiniteQuery(
        [API_ENDPOINTS.PRODUCTS, { language: locale }],
        ({ queryKey }) =>
          client.products.all({
            ...(queryKey[1] as ProductQueryOptions),
            ...query,
            name: searchTerm,
          })
      ),
      queryClient.prefetchInfiniteQuery(
        [API_ENDPOINTS.CATEGORIES, { limit: 1000, language: locale }],
        ({ queryKey }) =>
          client.categories.all(queryKey[1] as CategoryQueryOptions)
      ),
      queryClient.prefetchQuery(
        [API_ENDPOINTS.TAGS, { limit: 1000, language: locale }],
        ({ queryKey }) => client.tags.all(queryKey[1] as any)
      ),
      queryClient.prefetchInfiniteQuery(
        [API_ENDPOINTS.AI_MODELS, { language: locale }],
        ({ queryKey }) => client.aiModels.all(queryKey[1] as any)
      ),
    ]);

    return {
      props: {
        ...(await serverSideTranslations(locale!, ['common'])),
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
        searchTerm,
        description,
        revalidate: 60, // In seconds
      },
    };
  } catch (error) {
    console.log('Error fetching data:', error);
    //* if we get here, the product doesn't exist or something else went wrong
    return {
      notFound: true,
    };
  }
};

// TODO: SEO text gulo translation ready hobe kina? r seo text gulo static thakbe or dynamic?
const Marketplace: NextPageWithLayout = () => {
  const searchRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const debouncedSearch = router.query.search as string;
  const searchTerm = router.query.search as string;
  const [isSearchFocused] = useSearchFocus();
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'xs' || breakpoint === 'sm';
  
  return (
    <>
      <Seo
        title={
          searchTerm
            ? `Discover ${searchTerm} AI Prompts & Generators`
            : 'Find AI Prompts & Generators'
        }
        description={
          searchTerm
            ? `View all AI prompts & generators containing ${searchTerm}`
            : 'Discover prompts, generators & templates for Midjourney, ChatGPT, Leonardo AI, Flux AI, LoRA, and more. Supercharge your projects and achieve 10x success!'
        }
        url={routes.marketplace + (searchTerm ? `?search=${searchTerm}` : '')}
      />
      <SiteLinksSearchBoxJsonLd
        url="https://promptsclub.com/"
        potentialActions={[
          {
            target: 'https://promptsclub.com/products?search',
            queryInput: 'search_term_string',
          },
        ]}
      />

      {/* Modern Search Section */}
      <div className="w-full bg-gradient-to-b from-gray-50 to-white dark:from-black dark:to-black">
        <div className="container mx-auto max-w-[1340px] px-4 md:py-8">
          <div className="flex flex-col space-y-6">
            {/* Visually hidden heading for SEO */}
            <h1 className="sr-only">
              {searchTerm
                ? `Discover ${searchTerm} AI Prompts & Generators`
                : 'Find AI Prompts & Generators'}
            </h1>

            <ProductSearch
              searchRef={searchRef}
              context="marketplace"
              isInstantSearch={false}
            />

            {/* AI Models Tags */}
            <AIModelsList
              compact
              className={cn(
                'flex flex-wrap items-center gap-4 text-gray-600 dark:text-gray-400',
                {
                  hidden: isMobile && isSearchFocused,
                }
              )}
              hideViewAll={true}
            />
          </div>
        </div>
      </div>

      {/* Filter and Results Section */}
      <div className="container mx-auto max-w-[1340px] px-4 py-6 md:py-0 lg:py-6">
        <div
          className={cn('mb-6', {
            hidden: isMobile && isSearchFocused,
          })}
        >
          <ProductFilter />
        </div>

        <div className="flex flex-row gap-8">
          {/* Main Content */}
          <div className="min-w-0 flex-grow">
            <SearchResults searchText={debouncedSearch} />
          </div>

          {/* Sidebar Filters */}
          <div className="hidden w-[300px] flex-shrink-0 lg:block">
            <ProductFilterAccordions />
          </div>
        </div>
      </div>
    </>
  );
};

Marketplace.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

export default Marketplace;
